import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import TableFilters from "../TableFilters/TableFilters"
import {
  FILTER_OPERATOR,
  TColumnFiltersState,
  type TableVisualizationColumnFilter,
} from "~/ui-rtk/constants/table-visualization-types"
import { TFilterColumnConfig } from "../TableFilters/types"
import { CaptionPreview } from "../../common"
import { CloseSvg, EditSvg } from "../../svg/essentials"
import { useMemo, useState } from "react"
import { type TManualFilter } from "../../charts/TableVisualization/constants"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDate from "~/ui-rtk/hooks/date"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"

const dayjs = useDate()

export default function TableFiltersBar({
  id,
  columnConfig,
  additionalFilters = [],
  align = "right",
  className,
  getSelectOptions,
  isLoadingOptions,
}: {
  id: string
  columnConfig: TFilterColumnConfig[]
  additionalFilters?: TManualFilter[]
  align?: "left" | "right"
  className?: string
  getSelectOptions?: (columnKey: string) => TMultiSelectOption[]
  isLoadingOptions?: boolean
}) {
  const { columnFilters, setColumnFilters } = useUrlColumnFilters(id)
  const [filterToEditIdx, setFilterToEditIdx] = useState<number | null>(null)

  const filterToEdit = useMemo(() => {
    if (filterToEditIdx === null || !columnFilters) {
      return null
    }

    return {
      idx: filterToEditIdx,
      filter: columnFilters[filterToEditIdx],
    }
  }, [filterToEditIdx, columnFilters])

  const addFilterFn = (columnFilter: TableVisualizationColumnFilter) => {
    const filterToUpdate = [...(columnFilters ?? [])]
    const { idx, ...filter } = columnFilter
    if (typeof idx !== "undefined" && idx !== null) {
      filterToUpdate[idx] = filter
    } else {
      filterToUpdate.push(filter)
    }

    setColumnFilters([...filterToUpdate])
    setFilterToEditIdx(null)
  }
  const resetFilters = () => {
    setColumnFilters([])
  }

  const renderAppliedFilter = (
    filter: TableVisualizationColumnFilter,
    idx: number,
  ) => {
    const column = columnConfig.find(column => column.key === filter.id)
    const prefix = column?.header || filter.id

    let displayValue: string

    if (filter.variant === "range") {
      const { min, max } = filter.value as { min?: number; max?: number }
      displayValue = `from ${min !== undefined ? min : "—"} to ${max !== undefined ? max : "—"}`
    } else if (filter.variant === "text") {
      const filterValue = filter.value
      const filterOperator = filter.operator

      displayValue = `${FILTER_OPERATOR[filterOperator ?? "contains"]}: ${
        Array.isArray(filterValue)
          ? filterValue.join(", ")
          : (filterValue as string)
      }`
    } else if (filter.variant === "date") {
      const { startDate, endDate } = filter.value as {
        startDate?: Date | null | string
        endDate?: Date | null | string
      }

      const startStr = startDate ? dayjs(startDate).format("MMM D, YYYY") : "—"
      const endStr = endDate ? dayjs(endDate).format("MMM D, YYYY") : "—"
      displayValue = `from ${startStr} to ${endStr}`
    } else if (filter.variant === "select") {
      const filterValue = filter.value as string[]

      // For select filters, show labels instead of values if getSelectOptions is available
      if (getSelectOptions && filterValue.length > 0) {
        const options = getSelectOptions(filter.id)
        const selectedLabels = filterValue
          .map(
            value =>
              options.find(option => option.value === value)?.label || value,
          )
          .filter(Boolean)
        displayValue =
          selectedLabels.length > 0 ? selectedLabels.join(", ") : "No selection"
      } else {
        displayValue =
          filterValue.length > 0 ? filterValue.join(", ") : "No selection"
      }
    } else {
      return
    }

    const handleRemoveFilter = (idx: number) => () => {
      setColumnFilters((prev?: TColumnFiltersState) => {
        const prevFilters = [...(prev ?? [])]
        prevFilters.splice(idx, 1)
        return prevFilters
      })
    }

    const handleEditFilter = (idx: number) => () => {
      setFilterToEditIdx(idx)
    }

    return (
      <li
        key={`${filter.id}_${idx}`}
        className={cn(
          "flex bg-basic-dark-blue rounded-md px-3 py-2 gap-2",
          className,
        )}
      >
        <div>
          <span className="text-sm text-basic-grey-inactive">{prefix}:</span>{" "}
          <span className="text-3.5">
            <CaptionPreview text={displayValue} exact={true} align="left" />
          </span>
        </div>
        <button
          className="button button-circle"
          onClick={handleEditFilter(idx)}
        >
          <EditSvg />
        </button>
        <button
          className="button button-circle"
          onClick={handleRemoveFilter(idx)}
        >
          <CloseSvg />
        </button>
      </li>
    )
  }

  return (
    <div className="flex items-center justify-between h-12">
      {align === "right" ? (
        <ul className="flex gap-1 max-w-200 flex-wrap">
          {columnFilters?.map(renderAppliedFilter)}
        </ul>
      ) : null}
      {columnConfig && (
        <TableFilters
          columnConfig={columnConfig}
          filterToEdit={filterToEdit ?? undefined}
          additionalFilters={additionalFilters}
          onClose={() => setFilterToEditIdx(null)}
          onAddFilter={addFilterFn}
          onResetFilter={resetFilters}
          align={align}
          getSelectOptions={getSelectOptions}
          isLoadingOptions={isLoadingOptions}
        />
      )}
      {align === "left" ? (
        <ul className="flex gap-1 max-w-200 flex-wrap">
          {columnFilters?.map(renderAppliedFilter)}
        </ul>
      ) : null}
    </div>
  )
}
